import PropTypes from "prop-types";

import { gradeTranslation } from "../../utils/SchoolAdmin/translations";
import { buildApplicableSet, getGradeStatus } from "../../utils/teacher/gradeStatusUtils";

import "../../styles/TeacherHome.scss";

const CHIP_STYLES = {
    available: {
        icon: "bi-check-circle-fill",
        label: "Available"
    },
    "in-progress": {
        icon: "bi-pencil",
        label: "In Progress"
    },
    "not-started": {
        icon: "bi-dash-circle",
        label: "Not Started"
    }
};

/**
 * Displays per-grade Report Card status chips for the teacher home Updates panel.
 *
 * @name GradeStatusPanel
 * @param {Array}       applicableGrades          - grade tag objects from LocationDao (null = still loading)
 * @param {object|null} schoolLevelTemplateDto     - school template from global state
 * @param {object|null} districtTemplateDto        - district template from global state
 * @returns {JSX.Element|null}
 */
const GradeStatusPanel = ({ applicableGrades, schoolLevelTemplateDto, districtTemplateDto }) => {
    // Wait until applicable grades have loaded
    if (applicableGrades === null) return null;

    const applicableSet = buildApplicableSet(applicableGrades);
    const isApplicable = (gradeLevel) => applicableSet.has(gradeLevel);

    const semesterGrades = schoolLevelTemplateDto?.firstSemester?.gradeToSubjects ?? [];

    const gradeList =
        semesterGrades.length > 0
            ? semesterGrades
                  .filter((grade) => isApplicable(grade.gradeLevel))
                  .map((grade) => ({ gradeLevel: grade.gradeLevel, status: getGradeStatus(grade) }))
            : (districtTemplateDto?.gradeToSubjects || [])
                  .filter((grade) => isApplicable(grade.gradeLevel))
                  .map((grade) => ({ gradeLevel: grade.gradeLevel, status: "not-started" }));

    if (gradeList.length === 0) {
        return (
            <div className="no-grades-released">
                <i className="bi bi-hourglass-split" style={{ color: "#C8860A", marginRight: "8px" }} /> No grades have
                been released to teachers at this time. Please check back later.
            </div>
        );
    }

    return (
        <div className="released-status-container">
            <p className="grade-status-panel__title">School Template Status by Grade</p>
            <div className="grade-status-panel__chips">
                {gradeList.map(({ gradeLevel, status }) => {
                    const { icon, label } = CHIP_STYLES[status];
                    return (
                        <div key={gradeLevel} className={`grade-chip grade-chip--${status}`}>
                            <div className="grade-chip__grade">{gradeTranslation[gradeLevel]}</div>
                            <span className="grade-chip__status">
                                <i className={`bi ${icon}`} />
                                {label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

GradeStatusPanel.propTypes = {
    applicableGrades: PropTypes.arrayOf(PropTypes.shape({ tag: PropTypes.string })),
    districtTemplateDto: PropTypes.object,
    schoolLevelTemplateDto: PropTypes.object
};

export default GradeStatusPanel;
